import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import { MatSliderChange, MatSlider, MatSliderThumb } from '@angular/material/slider';
import { FlexModule } from '@angular/flex-layout';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton, MatIconAnchor } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SecondAsDurationPipe } from '../../pipes/second-as-duration.pipe';

@Component({
    selector: 'app-audio-control',
    template: `
    <!--<audio #audioElement controls [src]="src"></audio>-->
    <div [style.display]="isVisible ? 'block' : 'none'">
      <div class="controls" fxLayout="row" fxLayoutAlign="start center" fxFlexFill [matTooltip]="title">
        <button mat-icon-button (click)="playPause()">
          <mat-icon *ngIf="!isPlaying && !isLoading">play_arrow</mat-icon>
          <mat-icon *ngIf="isPlaying && !isLoading">pause</mat-icon>
          <mat-spinner [diameter]="20" style="margin: auto" [strokeWidth]="2" *ngIf="isLoading"></mat-spinner>
        </button>
        <div fxFlex="100%" fxLayout="row" fxLayoutAlign="start center" style="position: relative;">
          <div style="font-size: 12px; position: absolute; bottom: -5px; left: 5px">
          <span *ngIf="duration !== 0.01">
              {{currentTime | secondsAsDuration}}
          </span>
          </div>
          <mat-slider class="height-8" [disabled]="!isLoaded" [min]="0"
                      max="{{duration}}" style="width: 100%" #ngSlider><input matSliderThumb value="{{currentTime}}" (change)="currTimePosChanged({source: ngSliderThumb, parent: ngSlider, value: ngSliderThumb.value})" #ngSliderThumb="matSliderThumb" /></mat-slider>

          <div style="font-size: 12px; text-align: right; position: absolute; bottom: -5px; right: 5px">
          <span>
              {{duration | secondsAsDuration }}
          </span>
          </div>
        </div>
        <button mat-icon-button title="Replay" *ngIf="isPlaying || isLoaded" (click)="reloadAudio()" style="margin-left: 15px">
          <mat-icon>replay</mat-icon>
        </button>
        <a mat-icon-button title="Download" *ngIf="showDownload && isLoaded" target="_blank" href="{{downloadSrc}}"
           matTooltip="Download Recording">
          <mat-icon>download</mat-icon>
        </a>
        <button mat-icon-button title="Delete" *ngIf="showDelete && isLoaded" (click)="onDelete()" matTooltip="Delete Recording">
          <mat-icon>delete</mat-icon>
        </button>

        <button mat-icon-button title="Close" (click)="onClose()" style="margin-left: 15px" matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  `,
    styles: [
        '.controls {display: block; margin-bottom: 10px; background-color: #F1F5FA; padding: 10px; }',
        // '.card-title {font-size: 15px; display: flex;}',
        // '.expand-title {flex: auto; cursor: pointer;}',
        // '.card-title.collapsed {margin-bottom: -10px; }'
    ],
    standalone: true,
    imports: [FlexModule, MatTooltip, MatIconButton, NgIf, MatIcon, MatProgressSpinner, MatSlider, MatSliderThumb, MatIconAnchor, SecondAsDurationPipe]
})
export class AudioControlComponent implements OnChanges, OnInit, OnDestroy {

  @Input() title: string;
  @Input() src: string;
  @Input() downloadSrc: string;
  @Input() showDownload: boolean;
  @Input() showDelete: boolean;
  @Input() autoPlay: boolean;
  @Input() data: any;
  @Output() deleteEmitter = new EventEmitter<any>();
  @Output() errorEmitter = new EventEmitter<any>();
  @Output() onPlayEmitter = new EventEmitter<AudioControlComponent>();
  @Output() onCloseEmitter = new EventEmitter<boolean>();

  iOS = (/iPad|iPhone|iPod/.test(navigator.platform)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

  isPlaying = false;
  isLoading = false;
  isLoaded = false;
  isVisible = false;

  player?: HTMLAudioElement;
  durationToSet = -1;
  duration = 0;
  currentTime = 0;
  volume = 1;

  constructor() {

  }

  async playPause() {

    console.log('playPause: ' + this.src);
    if (!this.player) {
      console.error('player is not initialized');
    }

    this.isVisible = true;

    this.onPlayEmitter.emit(this);
    if (!this.isPlaying) {
      try {
        await this.player!.play();
        this.isLoading = false;
        this.isLoaded = true;
        this.isPlaying = true;
      } catch (e) {
        this.isPlaying = false;
        this.isLoading = false;
      }
    } else {
      this.pause();
    }
  }

  stop() {
    if (this.player) {
      console.log('stop player');
      this.player.pause();
      this.player.currentTime = 0;
      this.isPlaying = false;
      this.isLoaded = false;
    }
  }

  pause() {
    this.player!.pause();
    this.isPlaying = false;
  }

  async reloadAudio() {
    this.player!.currentTime = 0;
    await this.player!.play();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    console.log('AudioControl ngOnChanges');
    if (!this.data) {
      this.isVisible = false;
    }

    if (this.autoPlay && this.src) {
      try {
        this.stop();
        this.player!.src = this.src;
        this.player!.load();
        this.playPause();
      } catch (e) {
        console.log(e);
      }
    }
  }

  ngOnInit() {
    console.log('AudioControl ngOnInit');
    this.player = new Audio();
    this.bindPlayerEvent();
  }

  ngOnDestroy() {
    this.stop();
  }

  bindPlayerEvent(): void {
    if (!this.player) {
      return
    }

    this.player.addEventListener('playing', () => {
      this.isPlaying = true;
      this.duration = isFinite(this.player?.duration || NaN) ? Math.floor(this.player?.duration || NaN) : this.currentTime;
    });
    this.player.addEventListener('pause', () => {
      this.isPlaying = false;
    });
    this.player.addEventListener('timeupdate', () => {
      this.currentTime = Math.floor(this.player?.currentTime || NaN);
      // console.log('currentTime', this.currentTime, this.player.currentTime);

      if (this.duration < this.currentTime) {
        this.duration = this.currentTime;
      }

      // BUG: Commenting for `ended` event not firing #66
      // if (this.currentTime >= this.duration - this.endOffset) {
      //     this.player.nativeElement.pause();
      // }
    });
    this.player.addEventListener('volume', () => {
      this.volume = Math.floor(this.player?.volume || NaN);
    });
    if (!this.iOS) {
      this.player.addEventListener('loadstart', () => {
        this.isLoading = true;
      });
    }
    this.player.addEventListener('loadedmetadata', () => {
      this.isLoading = false;
      this.duration = isFinite(this.player?.duration || NaN) ? Math.floor(this.player?.duration || NaN) : this.currentTime;
    });
    this.player.addEventListener('ended', () => {
      console.log('paying is ended');
    });
    this.player.addEventListener('error', (err) => {
      console.log(err, this.player?.error);
      this.errorEmitter.emit(this.player?.error);
    });
  }

  onDelete() {
    this.deleteEmitter.emit(this.data);
  }

  currTimePosChanged($event: MatSliderChange) {
    console.log('set current time', $event.value);
    if (this.player) {
      this.player.currentTime = $event.value || 0;
    }
  }

  onClose() {
    this.isVisible = false;
    this.stop();
    this.onCloseEmitter.emit(true);
  }
}
